import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LuCreditCard, LuDownload, LuRefreshCw, LuTriangleAlert } from "react-icons/lu";
import { Link } from "react-router-dom";
import {
  api,
  type ConsoleBackfillReport,
  type ConsoleInvoiceHistoryRow,
  type ConsolePruneReport,
  type ConsoleReconcileReport,
  type ConsoleSubscriptionHistoryRow,
} from "@/lib/api";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Card, CardHeader, CardTitle, CardBody } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";

/**
 * Billing health. Everything here reads the local mirror of Stripe
 * (subscriptions / invoices), so it also doubles as a webhook monitor:
 * a stale "last webhook" timestamp means Stripe events are not landing.
 */
type Tab = "overview" | "subscriptions" | "invoices";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "subscriptions", label: "Subscriptions" },
  { id: "invoices", label: "Invoices" },
];

export function BillingPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [report, setReport] = useState<ConsoleReconcileReport | null>(null);
  const [backfill, setBackfill] = useState<ConsoleBackfillReport | null>(null);
  const [prune, setPrune] = useState<ConsolePruneReport | null>(null);

  const overview = useQuery({
    queryKey: ["console", "billing", "overview"],
    queryFn: () => api.billing.overview(),
  });

  const reconcile = useMutation({
    mutationFn: (dryRun: boolean) => api.billing.reconcile({ dryRun }),
    onSuccess: (res, dryRun) => {
      setReport(res);
      toast.success(
        dryRun
          ? `Dry run: ${res.changed} of ${res.checked} accounts would change`
          : `Reconciled ${res.checked} accounts, ${res.changed} corrected`,
      );
      if (!dryRun) {
        queryClient.invalidateQueries({ queryKey: ["console", "billing"] });
        queryClient.invalidateQueries({ queryKey: ["console", "users"] });
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const backfillMutation = useMutation({
    mutationFn: (applyTiers: boolean) => api.billing.backfill({ applyTiers }),
    onSuccess: (res) => {
      setBackfill(res);
      toast.success(
        `Recovered ${res.subscriptions} subscriptions and ${res.invoices} invoices` +
          (res.tiersApplied ? `, repaired ${res.tiersApplied} accounts` : ""),
      );
      queryClient.invalidateQueries({ queryKey: ["console", "billing"] });
      queryClient.invalidateQueries({ queryKey: ["console", "users"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const pruneMutation = useMutation({
    mutationFn: (dryRun: boolean) => api.billing.pruneForeign({ dryRun }),
    onSuccess: (res) => {
      setPrune(res);
      toast.success(
        res.dryRun
          ? `Dry run: ${res.subscriptions} subscriptions and ${res.invoices} invoices would be removed`
          : `Removed ${res.subscriptions} subscriptions and ${res.invoices} invoices`,
      );
      if (!res.dryRun) queryClient.invalidateQueries({ queryKey: ["console", "billing"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const data = overview.data;
  const webhookAge = data?.lastWebhookAt
    ? Date.now() - new Date(data.lastWebhookAt).getTime()
    : null;
  const webhookStale = webhookAge === null || webhookAge > 7 * 24 * 60 * 60 * 1000;

  return (
    <>
      <PageHeader
        title="Billing"
        icon={LuCreditCard}
        description="Revenue, dunning and Stripe webhook health"
      />

      <div className="px-8 pt-6">
        <div className="flex gap-1 border-b border-[var(--color-border)]">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[var(--color-accent)] text-[var(--color-text)]"
                  : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "subscriptions" && <SubscriptionHistory />}
      {tab === "invoices" && <InvoiceHistory />}

      <div className={tab === "overview" ? "p-8 space-y-6" : "hidden"}>
        {webhookStale && (
          <Card className="border-[rgba(253,186,116,0.35)]">
            <CardBody className="flex items-start gap-3 text-sm">
              <LuTriangleAlert className="w-4 h-4 mt-0.5 text-[#fdba74] shrink-0" />
              <div className="space-y-1">
                <p className="text-[var(--color-text)]">
                  No Stripe webhook received{" "}
                  {data?.lastWebhookAt
                    ? `since ${new Date(data.lastWebhookAt).toLocaleString()}`
                    : "ever"}
                  .
                </p>
                <p className="text-[var(--color-text-muted)]">
                  Check the endpoint in Stripe points at
                  https://destroy.network/api/webhooks/stripe, is in live mode, has a
                  matching signing secret, and subscribes to checkout.session.completed,
                  customer.subscription.*, invoice.payment_failed and
                  invoice.payment_succeeded. Until then, plans stay correct only via the
                  nightly reconcile below.
                </p>
              </div>
            </CardBody>
          </Card>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Stat
            label="MRR"
            value={data ? `$${data.mrr.toLocaleString()}` : "-"}
            hint="From live Stripe subscriptions, yearly amortised"
          />
          <Stat
            label="Past due accounts"
            value={data ? String(data.pastDueAccounts) : "-"}
            hint="In dunning; downgraded after the grace window"
          />
          <Stat
            label="Last invoice"
            value={data?.lastInvoiceAt ? new Date(data.lastInvoiceAt).toLocaleDateString() : "none"}
            hint="Most recent invoice event mirrored locally"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Subscriptions by tier</CardTitle>
          </CardHeader>
          <CardBody>
            {!data || Object.keys(data.byTier).length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                No active subscriptions mirrored yet. Run a reconcile to pull current
                state from Stripe.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {Object.entries(data.byTier).map(([tier, row]) => (
                  <li key={tier} className="flex items-center justify-between">
                    <span className="uppercase tracking-wider text-xs">{tier}</span>
                    <span className="text-[var(--color-text-muted)]">
                      {row.subs} sub{row.subs === 1 ? "" : "s"} - $
                      {(row.mrrCents / 100).toFixed(2)}/mo
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Recover history from Stripe</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={backfillMutation.isPending}
                onClick={() => backfillMutation.mutate(false)}
              >
                <LuDownload className="w-3.5 h-3.5" />
                Import only
              </Button>
              <Button
                size="sm"
                disabled={backfillMutation.isPending}
                onClick={() => backfillMutation.mutate(true)}
              >
                Import and repair tiers
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-[var(--color-text-muted)]">
              Walks this product's own customers and imports their subscriptions and
              invoices, so past payments show up in the console. The Stripe account is
              shared with other Buun Group products; anything belonging to those is
              skipped. "Import and repair tiers" also resets each account's plan to what
              Stripe says - the fix for accounts whose webhooks never arrived. Operator
              comps are left alone.
            </p>
            {backfill && (
              <div className="text-sm text-[var(--color-text-muted)] space-y-1">
                <p>
                  {backfill.customersChecked} customers checked - {backfill.subscriptions}{" "}
                  subscriptions, {backfill.invoices} invoices, {backfill.tiersApplied}{" "}
                  accounts repaired
                </p>
                {backfill.skippedForeign > 0 && (
                  <p className="text-xs">
                    {backfill.skippedForeign} subscription
                    {backfill.skippedForeign === 1 ? "" : "s"} skipped - they belong to
                    another product on this Stripe account.
                  </p>
                )}
                {backfill.errors.slice(0, 5).map((err) => (
                  <p key={err} className="text-xs text-[var(--color-error)]">
                    {err}
                  </p>
                ))}
              </div>
            )}

            <div className="border-t border-[var(--color-border)] pt-3 space-y-2">
              <p className="text-sm text-[var(--color-text-muted)]">
                Clean up rows that belong to another product sharing this Stripe account.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pruneMutation.isPending}
                  onClick={() => pruneMutation.mutate(true)}
                >
                  Check for foreign rows
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={pruneMutation.isPending || !prune || prune.subscriptions + prune.invoices === 0}
                  onClick={() => pruneMutation.mutate(false)}
                >
                  Remove them
                </Button>
              </div>
              {prune && (
                <p className="text-xs text-[var(--color-text-muted)]">
                  {prune.dryRun ? "Would remove" : "Removed"} {prune.subscriptions}{" "}
                  subscriptions and {prune.invoices} invoices.
                </p>
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Reconcile with Stripe</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={reconcile.isPending}
                onClick={() => reconcile.mutate(true)}
              >
                Dry run
              </Button>
              <Button
                size="sm"
                disabled={reconcile.isPending}
                onClick={() => reconcile.mutate(false)}
              >
                <LuRefreshCw
                  className={`w-3.5 h-3.5 ${reconcile.isPending ? "animate-spin" : ""}`}
                />
                Run now
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-[var(--color-text-muted)]">
              Re-derives every paying account's tier from Stripe and mirrors current
              subscriptions. Accounts with a failed payment are dropped to free until the
              payment goes through. Runs nightly at 03:00 UTC; this button runs it now.
            </p>
            {report && (
              <div className="text-sm space-y-2">
                <p className="text-[var(--color-text-muted)]">
                  Checked {report.checked}, changed {report.changed}, errors {report.errors}
                </p>
                {report.changes.length > 0 && (
                  <ul className="space-y-1">
                    {report.changes.map((ch) => (
                      <li key={`${ch.userId}-${ch.reason}`} className="text-xs">
                        <span className="text-[var(--color-text)]">{ch.email}</span>{" "}
                        <span className="text-[var(--color-text-muted)]">
                          {ch.from} to {ch.to} ({ch.reason})
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card>
      <CardBody>
        <div className="text-[11px] uppercase tracking-wider text-[var(--color-text-subtle)]">
          {label}
        </div>
        <div className="text-2xl font-semibold text-[var(--color-text)] tabular-nums mt-1">
          {value}
        </div>
        <div className="text-xs text-[var(--color-text-muted)] mt-1">{hint}</div>
      </CardBody>
    </Card>
  );
}

/**
 * Every subscription we have ever mirrored, current and past. Sourced
 * from the local mirror rather than Stripe, so filtering and paging
 * cost a single D1 query.
 */
function SubscriptionHistory() {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const query = useQuery({
    queryKey: ["console", "billing", "subscriptions", { status, q }],
    queryFn: () =>
      api.billing.subscriptions({ status, q: q || undefined, limit: 100 }),
  });

  const columns: Column<ConsoleSubscriptionHistoryRow>[] = [
    {
      header: "Account",
      cell: (row) =>
        row.user_id ? (
          <Link
            to={`/users/${row.user_id}`}
            className="text-[var(--color-text)] hover:underline"
          >
            {row.email ?? row.user_id}
          </Link>
        ) : (
          <span className="text-[var(--color-text-subtle)]" title={row.stripe_customer_id}>
            unlinked
          </span>
        ),
    },
    { header: "Tier", cell: (row) => <span className="uppercase text-xs">{row.tier}</span> },
    { header: "Status", cell: (row) => <SubStatus row={row} /> },
    {
      header: "Amount",
      cell: (row) => (
        <span className="tabular-nums">
          {money(row.amount_cents, row.currency)}
          {row.interval ? `/${row.interval}` : ""}
        </span>
      ),
    },
    {
      header: "Period end",
      cell: (row) => (
        <span className="text-xs text-[var(--color-text-muted)] tabular-nums">
          {row.current_period_end
            ? new Date(row.current_period_end).toLocaleDateString()
            : "-"}
        </span>
      ),
    },
    {
      header: "Subscription",
      cell: (row) => (
        <span className="font-mono text-[11px] text-[var(--color-text-subtle)]">{row.id}</span>
      ),
    },
  ];

  return (
    <div className="p-8 space-y-4">
      <HistoryFilters
        q={q}
        onQ={setQ}
        status={status}
        onStatus={setStatus}
        options={[
          { value: "all", label: "All statuses" },
          { value: "live", label: "Active / trialing" },
          { value: "past_due", label: "Past due" },
          { value: "ended", label: "Ended" },
        ]}
        count={query.data?.total}
        noun="subscription"
      />
      <Card>
        <DataTable
          columns={columns}
          rows={query.data?.subscriptions}
          isLoading={query.isLoading}
          isError={query.isError}
          rowKey={(row) => row.id}
          emptyMessage="No subscriptions mirrored yet. Run an import from the Overview tab."
        />
      </Card>
    </div>
  );
}

/** Invoice history: what was collected, what failed, and when. */
function InvoiceHistory() {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const query = useQuery({
    queryKey: ["console", "billing", "invoices", { status, q }],
    queryFn: () => api.billing.invoices({ status, q: q || undefined, limit: 100 }),
  });

  const columns: Column<ConsoleInvoiceHistoryRow>[] = [
    {
      header: "Account",
      cell: (row) =>
        row.user_id ? (
          <Link
            to={`/users/${row.user_id}`}
            className="text-[var(--color-text)] hover:underline"
          >
            {row.email ?? row.user_id}
          </Link>
        ) : (
          <span className="text-[var(--color-text-subtle)]">unlinked</span>
        ),
    },
    {
      header: "Date",
      cell: (row) => (
        <span className="tabular-nums text-xs text-[var(--color-text-muted)]">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: "Status",
      cell: (row) => (
        <span
          className={`text-xs uppercase ${
            row.failed_at || row.status === "uncollectible"
              ? "text-[#fdba74]"
              : row.status === "paid"
                ? "text-[#6ee7b7]"
                : "text-[var(--color-text-muted)]"
          }`}
        >
          {row.status ?? (row.failed_at ? "failed" : "-")}
          {row.attempt_count > 1 ? ` (${row.attempt_count} attempts)` : ""}
        </span>
      ),
    },
    {
      header: "Paid",
      cell: (row) => (
        <span className="tabular-nums">{money(row.amount_paid, row.currency)}</span>
      ),
    },
    {
      header: "Due",
      cell: (row) => (
        <span className="tabular-nums text-[var(--color-text-muted)]">
          {money(row.amount_due, row.currency)}
        </span>
      ),
    },
    {
      header: "",
      cell: (row) =>
        row.hosted_invoice_url ? (
          <a
            href={row.hosted_invoice_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs underline text-[var(--color-text-muted)]"
          >
            view
          </a>
        ) : null,
    },
  ];

  return (
    <div className="p-8 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Stat
          label="Collected (lifetime)"
          value={query.data ? money(query.data.collectedCents, "usd") : "-"}
          hint="Sum of every paid invoice in the mirror"
        />
        <Stat
          label="Failed (lifetime)"
          value={query.data ? money(query.data.failedCents, "usd") : "-"}
          hint="Amount due on invoices that failed to collect"
        />
      </div>
      <HistoryFilters
        q={q}
        onQ={setQ}
        status={status}
        onStatus={setStatus}
        options={[
          { value: "all", label: "All statuses" },
          { value: "paid", label: "Paid" },
          { value: "failed", label: "Failed" },
          { value: "open", label: "Open" },
        ]}
        count={query.data?.total}
        noun="invoice"
      />
      <Card>
        <DataTable
          columns={columns}
          rows={query.data?.invoices}
          isLoading={query.isLoading}
          isError={query.isError}
          rowKey={(row) => row.id}
          emptyMessage="No invoices mirrored yet. Run an import from the Overview tab."
        />
      </Card>
    </div>
  );
}

function HistoryFilters({
  q,
  onQ,
  status,
  onStatus,
  options,
  count,
  noun,
}: {
  q: string;
  onQ: (value: string) => void;
  status: string;
  onStatus: (value: string) => void;
  options: { value: string; label: string }[];
  count: number | undefined;
  noun: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        type="search"
        placeholder="Search by email or id"
        value={q}
        onChange={(e) => onQ(e.target.value)}
        className="flex-1 max-w-sm min-w-[200px] px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)]"
      />
      <select
        value={status}
        onChange={(e) => onStatus(e.target.value)}
        className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {count !== undefined && (
        <span className="text-xs text-[var(--color-text-muted)]">
          {count} {noun}
          {count === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );
}

function SubStatus({ row }: { row: ConsoleSubscriptionHistoryRow }) {
  const live = ["active", "trialing"].includes(row.status);
  const dunning = ["past_due", "unpaid"].includes(row.status);
  const cls = live
    ? "text-[#6ee7b7]"
    : dunning
      ? "text-[#fdba74]"
      : "text-[var(--color-text-subtle)]";
  return (
    <span className={`text-xs flex items-center gap-1.5 ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {row.status}
      {row.cancel_at_period_end === 1 && live ? " (cancels)" : ""}
    </span>
  );
}

function money(cents: number | null, currency: string | null): string {
  if (cents == null) return "-";
  return `$${(cents / 100).toFixed(2)}${
    currency && currency.toLowerCase() !== "usd" ? ` ${currency.toUpperCase()}` : ""
  }`;
}
